'use strict';

/*
  VerifiedByVisaService

  Handles communication between FE UI and backend Verified By Visa endpoints.

  Parameters:
    presenter [APIPresenter] (required):
      Reference to the APIPresenter in order to communicate with SOA and create data models.
*/

function VerifiedByVisaService(presenter) {
  this._presenter = presenter;
  this._analytics = new Analytics();

  // Constants
  this._verifyRelLink = 'card.visa_direct_verify';
  this._statusRelLink = 'card.poll_authentication_status';
  this._windowSizes = {
    xlarge: '01', // 250x400
    large: '01', // 250x400
    medium: '02', // 390x400
    small: '01' // 250x400
  };
  this._statuses = {
    CHALLENGE_REQUIRED: 'challenge_required',
    UPDATE_PENDING: 'fetch_updates_failed',
    CHALLENGE_SUCCESS: 'authenticated',
    CHALLENGE_FAILURE: 'authentication_failed',
    CHALLENGE_INCOMPLETE: 'not_authenticated',
    CHALLENGE_ACKNOWLEDGED: 'authentication_attempt_acknowledged',
    TIMEOUT: 'timeout'
  };
  this._timeoutLengthInMinutes = 10; // TODO: confirm actual timeout
  this._uiContainer = null;
  this._postedToIframe = false;

  // Parameters / variables
  this._challengeInitiatedCallback = function () {};
  this._successCallback = function () {};
  this._failureCallback = function () {};
  this._transferAmount = 0;
  this._viewport = document;
  this._timeoutTime = null;
  this._challengeId = '';
  this._request = null;
  this._requestFormsIframes = [];

  // Public methods
  this.init = function () {
    var _ref = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : settingsObj,
        transferAmount = _ref.transferAmount,
        viewport = _ref.viewport,
        challengeInitiatedCallback = _ref.challengeInitiatedCallback,
        successCallback = _ref.successCallback,
        failureCallback = _ref.failureCallback;

    this._transferAmount = transferAmount;
    this._viewport = viewport;
    if (challengeInitiatedCallback) this._challengeInitiatedCallback = challengeInitiatedCallback;
    this._successCallback = successCallback;
    this._failureCallback = failureCallback;
    this._getChallengeId();
  };

  // Private methods
  this._getChallengeId = function () {
    var _this = this;

    if (this._presenter.hasAction(this._verifyRelLink)) {
      var payload = {
        vd_financial_account_id: this._presenter.getModelProperty('banking.financial_accounts.visa_direct_financial_accounts.0.id'),
        amount: this._transferAmount,
        browser: this._getBrowserInfo(),
        challenge_window_size: this._getChallengeWindowSize(this._viewport)
      };
      var actionRequest = this._presenter.performAction(this._verifyRelLink, payload, function (data, response) {
        if (response === 200) {
          // Authentication process has started - go to step 2
          if (data.data.status === 'authenticate_initiated') {
            if (!_this._postedToIframe && data.data && data.data.id) {
              _this._challengeId = data.data.id;
              if (data.data.method_url && data.data.three_ds_method_data && data.data.method_url != null && data.data.three_ds_method_data != null) {
                _this._postToIframe({
                  uri: data.data.method_url,
                  dataParam: 'threeDSMethodData',
                  dataValue: data.data.three_ds_method_data
                });
              }
              _this._postedToIframe = true;
            }
            _this._doAuthenticateInitiated(actionRequest);

            // Issuer does not currently support 3DSecure V2
          } else if (data.data.status === 'tds_vs_unsupported') {
            _this._successCallback(data.data.id);

            // Failed to start authentication status
          } else {
            _this._failureCallback();
          }
        } else {
          _this._failureCallback();
        }
      });
    }
  };

  this._doAuthenticateInitiated = function (requestObj) {
    this._checkForChallenge();
  };

  this._checkForChallenge = function () {
    var _this2 = this;

    if (this._presenter.hasAction(this._statusRelLink)) {
      this._request = this._presenter.performAction(this._statusRelLink, { three_d_s_id: this._challengeId }, function (data, response) {
        // clear any forms and iframes used to make previous requests
        _this2._clearRequestFormsIframes();

        if (response === 200) {
          var status = _this2._getStatus(data);
          if (status === _this2._statuses.CHALLENGE_REQUIRED) {
            // Transaction challenged
            _this2._challengeInitiatedCallback();
            _this2._showChallengeUI(_this2._getChallengeUrl(data), _this2._getCreq(data));
            _this2._startPollForStatus();
          } else if (status === _this2._statuses.CHALLENGE_FAILURE || status === _this2._statuses.CHALLENGE_INCOMPLETE) {
            _this2._failureCallback();
          } else if (status === _this2._statuses.CHALLENGE_ACKNOWLEDGED || status === _this2._statuses.CHALLENGE_SUCCESS) {
            if (data.data.chargeback_protection_available == true) {
              // Transaction not challenged
              _this2._successCallback(_this2._challengeId);
            } else {
              _this2._failureCallback();
            }
          } else {
            // NO-OP
          }
        }
      });
    }
  };

  this._startPollForStatus = function () {
    this._timeoutTime = moment().add(this._timeoutLengthInMinutes, 'minutes');
    this._pollForStatus();
  };

  this._pollForStatus = function () {
    var _this3 = this;

    if (this._presenter.hasAction(this._statusRelLink)) {
      this._request = this._presenter.performAction(this._statusRelLink, { three_d_s_id: this._challengeId, challenge_accepted: true }, function (data, response) {
        if (moment().isAfter(_this3._timeoutTime)) {
          // Timing out and redirecting to fail screen after 10 minutes
          _this3._failureCallback();
        } else if (response === 408) {
          // overriding 15 second FE timeout - continue polling
          _this3._pollForStatus();
        } else if (response === 200) {
          var _statuses = _this3._statuses,
              CHALLENGE_REQUIRED = _statuses.CHALLENGE_REQUIRED,
              CHALLENGE_SUCCESS = _statuses.CHALLENGE_SUCCESS,
              CHALLENGE_FAILURE = _statuses.CHALLENGE_FAILURE,
              CHALLENGE_INCOMPLETE = _statuses.CHALLENGE_INCOMPLETE,
              UPDATE_PENDING = _statuses.UPDATE_PENDING,
              CHALLENGE_ACKNOWLEDGED = _statuses.CHALLENGE_ACKNOWLEDGED,
              TIMEOUT = _statuses.TIMEOUT;


          switch (_this3._getStatus(data)) {
            case CHALLENGE_SUCCESS:
            case CHALLENGE_ACKNOWLEDGED:
              _this3._successCallback(_this3._challengeId);
              break;
            case CHALLENGE_REQUIRED:
            case UPDATE_PENDING:
              _this3._pollForStatus();
              break;
            case CHALLENGE_FAILURE:
            case CHALLENGE_INCOMPLETE:
            case TIMEOUT:
            default:
              _this3._failureCallback();
              break;
          }
        }
      });
    }
  };

  this._showChallengeUI = function (challengeUrl, creq) {
    // remove any existing iframe
    if (this._uiContainer) this._uiContainer.remove();

    this._postToIframe({
      container: document.querySelector('.js--challenge-iframe-container'),
      uri: challengeUrl,
      dataParam: 'creq',
      dataValue: creq,
      iframeClasses: ['vbv-challenge-iframe']
    });

    this._uiContainer = document.querySelector('.vbv-challenge-iframe');
  };

  // Makes a POST request by building and submitting a form to an iframe
  // By default, the elements are hidden and appended to the body
  this._postToIframe = function () {
    var _ref2 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : settings,
        _ref2$uri = _ref2.uri,
        uri = _ref2$uri === undefined ? console.warn('request uri is required') : _ref2$uri,
        _ref2$dataParam = _ref2.dataParam,
        dataParam = _ref2$dataParam === undefined ? console.warn('request data parameter is required') : _ref2$dataParam,
        _ref2$dataValue = _ref2.dataValue,
        dataValue = _ref2$dataValue === undefined ? console.warn('request data value is required') : _ref2$dataValue,
        _ref2$iframeClasses = _ref2.iframeClasses,
        iframeClasses = _ref2$iframeClasses === undefined ? ['visually-hidden'] : _ref2$iframeClasses,
        _ref2$container = _ref2.container,
        container = _ref2$container === undefined ? document.querySelector('body') : _ref2$container;

    var postForm = document.createElement('form');
    postForm.setAttribute('method', 'POST');
    postForm.setAttribute('action', uri);
    postForm.setAttribute('target', dataParam + 'Iframe');
    postForm.classList.add('visually-hidden');

    var postInput = document.createElement('input');
    postInput.setAttribute('type', 'hidden');
    postInput.setAttribute('name', dataParam);
    postInput.setAttribute('value', dataValue);

    var postIframe = document.createElement('iframe');
    postIframe.setAttribute('name', dataParam + 'Iframe');
    postIframe.classList.add(iframeClasses);

    postForm.appendChild(postInput);
    container.appendChild(postForm);
    container.appendChild(postIframe);
    postForm.submit();

    this._requestFormsIframes.push(postForm, postIframe);
  };

  // Utilities
  this._getBrowserInfo = function () {
    var browserLanguage = navigator.language ? navigator.language : navigator.browserLanguage ? navigator.browserLanguage : 'en-US';
    return {
      browser_accept_header: 'application/json',
      browser_java_enabled: navigator.javaEnabled(),
      browser_javascript_enabled: true,
      browser_language: browserLanguage,
      browser_color_depth: '' + screen.colorDepth,
      browser_tz: '' + new Date().getTimezoneOffset(),
      browser_screen_width: '' + this._getViewportWidth(),
      browser_screen_height: '' + this._getViewportHeight(),
      browser_user_agent: navigator.userAgent
    };
  };

  this._getChallengeWindowSize = function () {
    var width = this._getViewportWidth(this._viewport);
    var height = this._getViewportHeight(this._viewport);
    var windowSize = this._windowSizes.small;
    if (width <= window.mogo.registry.mogoUI.config.mediaQueries.small) {
      windowSize = this._windowSizes.small;
    } else if (width <= window.mogo.registry.mogoUI.config.mediaQueries.medium) {
      windowSize = this._windowSizes.medium;
    } else if (width <= this._magicXLargeWindowWidth) {
      windowSize = this._windowSizes.large;
    } else {
      windowSize = this._windowSizes.xlarge;
    }
    return windowSize;
  };

  this._getViewportWidth = function (viewport) {
    return viewport ? viewport.offsetWidth : Math.max(document.body.scrollWidth, document.documentElement.scrollWidth, document.body.offsetWidth, document.documentElement.offsetWidth, document.documentElement.clientWidth);
  };

  this._getViewportHeight = function (viewport) {
    return viewport ? viewport.offsetHeight : Math.max(document.body.scrollHeight, document.documentElement.scrollHeight, document.body.offsetHeight, document.documentElement.offsetHeight, document.documentElement.clientHeight);
  };

  this._getStatus = function (data) {
    if (data && data.data && data.data.status) {
      return data.data.status;
    }
  };

  this._getChallengeUrl = function (data) {
    if (data && data.data && data.data.acs_url) {
      return data.data.acs_url;
    }
  };

  this._getCreq = function (data) {
    if (data && data.data && data.data.creq) {
      return data.data.creq;
    }
  };

  this._clearRequestFormsIframes = function () {
    if (this._requestFormsIframes.length > 0) {
      this._requestFormsIframes.forEach(function (el) {
        return el.remove();
      });
    }
  };

  this.destroy = function () {
    if (this._request) this._request.cancel();
  };
}